import _ from 'underscore';
import React, { useCallback, useContext, useMemo } from 'react';
import { Caption1Strong, mergeClasses } from '@fluentui/react-components';
import PropTypes from 'prop-types';

import { StepHeader } from '@bingads-webui-campaign-react/linkedin-shared-components';
import { useWizardService, WIZARD_EVENTS } from '@bingads-webui-fluent-component/wizard';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { FormErrorGroup, UnmappedFormErrors } from '@bingads-webui-campaign-react/form-error';
import { withControlErrorHandling } from '@bingads-webui-campaign-react/error-handling';
import { useFormService, FORM_STATES } from '@bingads-webui-fluent-component/form';
import { ValidationGroup } from '@bingads-webui-react/validation';
import { Collapse, useCollapseState, collapseExpander, getPanelErrorProps } from '@bingads-webui-campaign/wizard-jumbo-card';

import { CollapsibleLocations } from './cards/collapsible-locations';
import { CollapsibleLanguage } from './cards/collapsible-language';
import { CollapsibleAudiences } from './cards/collapsible-audiences';
import { CollapsibleGender } from './cards/collapsible-gender';
import { CollapsibleAge } from './cards/collapsible-age';
import { CollapsibleConversionGoals } from './cards/collapsible-conversion-goals';
import { useTargetingStepStyles } from './styles/targeting-step-styles';

const UnmappedFormServerErrors = withControlErrorHandling(UnmappedFormErrors);

const AllTargetingSections = ['locations', 'language', 'audiences', 'gender', 'age', 'conversion-goals'];
const InitiallyExpandedTargetingSections = ['locations', 'language', 'audiences', 'conversion-goals'];

export const TargetingStep = ({ data, error, onDataChange }) => {
  const i18n = useContext(I18nContext);
  const classes = useTargetingStepStyles();

  const { scope } = useContext(ScopeContext);
  const { send } = useWizardService();

  const {
    ageRanges, genders, defaultLocation, hasLinkedInCompatibleGoals,
  } = data || {};
  const { serverErrors = [] } = error || {};

  const { state } = useFormService();
  const { context: { saveFailed } } = state;
  const isSaving = state.matches(FORM_STATES.SAVING);
  const isLoading = state.matches(FORM_STATES.LOADING);

  const showAllValidationErrors = !_.isEmpty(serverErrors) || saveFailed;
  const areBeaconsActive = showAllValidationErrors && !isSaving;

  const [
    allSections,
    expandedSections,
    setExpandedSections,
  ] = useCollapseState(AllTargetingSections, InitiallyExpandedTargetingSections);

  const expandSections = useMemo(() =>
    collapseExpander({ setActiveKeys: setExpandedSections }), [setExpandedSections]);

  const onValidate = useCallback(({ data: newData, isValid }) => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        ...newData,
      }),
      isValid,
    });
  }, [onDataChange]);

  const navigateToConversionGoals = useCallback(() => {
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { onExited: () => scope.save('/campaign/vnext/conversiongoals') });
    send(WIZARD_EVENTS.CANCEL);
  }, [send, scope]);

  return (
    <div className={mergeClasses('li-targeting-step', classes.root)}>
      <StepHeader
        title={_TL_('Targeting & conversions')}
        description={_TL_('Add your campaign’s targeting and conversion goal settings.')}
      />
      <ActuationGroup actuationProps={{ isActive: areBeaconsActive }}>
        <AttentionBeaconGroup>
          <FormErrorGroup formErrorProps={{ errors: serverErrors, showAllValidationErrors }}>
            <ValidationGroup validationProps={{ data, onValidate }}>
              <UnmappedFormServerErrors className="unmapped-server-errors" />
              <Collapse
                className={classes.collapseContainer}
                allPanelItems={allSections}
                activeKeys={expandedSections}
                setActiveKeys={setExpandedSections}
                skipItemsTypeCheck
              >
                <CollapsibleLocations
                  key="locations"
                  className="locations-card"
                  defaultLocation={defaultLocation}
                  isLoading={isLoading}
                  attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                  actuationProps={{
                    expandKey: 'locations',
                    expand: expandSections,
                  }}
                />
                <CollapsibleLanguage
                  key="language"
                  className="language-card"
                  isLoading={isLoading}
                  attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                  actuationProps={{
                    expandKey: 'language',
                    expand: expandSections,
                  }}
                />
                <CollapsibleAudiences
                  key="audiences"
                  className="audiences-card"
                  isLoading={isLoading}
                  attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                  actuationProps={{
                    expandKey: 'audiences',
                    expand: expandSections,
                  }}
                />
                <CollapsibleGender
                  key="gender"
                  className="gender-card"
                  genders={genders}
                  isLoading={isLoading}
                  attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                  actuationProps={{
                    expandKey: 'gender',
                    expand: expandSections,
                  }}
                />
                <CollapsibleAge
                  key="age"
                  className="age-card"
                  ageRanges={ageRanges}
                  isLoading={isLoading}
                  attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                  actuationProps={{
                    expandKey: 'age',
                    expand: expandSections,
                  }}
                />
                <CollapsibleConversionGoals
                  key="conversion-goals"
                  className="conversion-goals-card"
                  hasLinkedInCompatibleGoals={hasLinkedInCompatibleGoals}
                  isLoading={isLoading}
                  onNavigateToConversionGoalsClicked={navigateToConversionGoals}
                  attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                  actuationProps={{
                    expandKey: 'conversion-goals',
                    expand: expandSections,
                  }}
                />
                <Caption1Strong>
                  {i18n.getString(_TL_('I certify that I will not use LinkedIn to discriminate based on age, gender or other protected characteristics in ads relating to employment, housing, education or credit.'))}
                </Caption1Strong>
              </Collapse>
            </ValidationGroup>
          </FormErrorGroup>
        </AttentionBeaconGroup>
      </ActuationGroup>
    </div>
  );
};

TargetingStep.propTypes = {
  data: PropTypes.shape({}).isRequired,
  error: PropTypes.shape({}).isRequired,
  onDataChange: PropTypes.func.isRequired,
};
